<template>
	<ext-dialog
		v-model="value"
		:title="$t('product')"
		:primaryButtonText="$t('save')"
		@primaryButtonAction="saveAction"
		@closed="$emit('input', false)"
	>
		<template #body v-if="currentItem">
			<v-form ref="form" v-model="valid" lazy-validation>
				<product-select
					:key="productSelectKey"
					v-model="currentItem.variantInfo"
					:label="$t('product')"
					:rules="rules.variant"
					eager
					@change="variantChangeAction"
					hide-details
				/>

				<v-select
					v-model="currentItem.unitCode"
					:items="unitOptions"
					item-text="code"
					item-value="code"
					:label="$t('unit')"
					:rules="rules.unit"
					:disabled="!currentItem.variantId"
					outlined
					append-icon="icon-angle-down"
					hide-details
				/>

				<v-divider v-if="currentItem.unitCode" class="mt-2 mb-3" />
				<div v-if="currentItem.unitCode">
					<v-row dense v-if="activeFormat && activeFormat.inputs.length">
						<v-col v-for="f in activeFormat.inputs" :key="f" cols="12" sm="6" md="4">
							<v-text-field
								v-model.number="measurementInputs[f]"
								:label="$t(f)"
								type="number"
								:disabled="fieldLocked(f)"
								hide-details
							/>
						</v-col>
						<v-col cols="12" sm="4" md="3">
							<v-select
								v-model="selectedUnitForField"
								:items="[{ code: selectedUnitForField }]"
								item-text="code"
								item-value="code"
								:label="$t('unit')"
								disabled
								append-icon="icon-angle-down"
								hide-details
							/>
						</v-col>
					</v-row>
					<v-row dense v-else>
						<v-col cols="12" sm="6" md="4">
							<v-text-field
								v-model.number="scalarValue"
								:label="$t('fixed_value')"
								type="number"
								:disabled="scalarLocked"
								hide-details
							/>
						</v-col>
					</v-row>
					<v-divider class="my-3" />
				</div>

				<v-row>
					<v-col>
						<v-text-field
							v-model.number="currentItem.quantity"
							:label="$t('quantity')"
							:rules="rules.quantity"
							type="number"
							min="0"
							hide-details
						/>
					</v-col>
				</v-row>

				<v-text-field
					v-model.number="currentItem.amount"
					:label="$t('amount')"
					:rules="rules.amount"
					type="number"
					min="0"
					:suffix="shipment.currency.symbol"
					hide-details
				/>
			</v-form>
		</template>
	</ext-dialog>
</template>

<script>
import cloneDeep from "lodash.clonedeep";
import remote from "../../../../data/remote";
import uiMixin from "../../../../store/interfaces/ui.mixin";
import ProductSelect from "../../../controls/ProductSelect.vue";
import ExtDialog from "../../../partials/dialogs/ExtDialog.vue";

export default {
	mixins: [uiMixin],
	components: { ProductSelect, ExtDialog },
	props: { value: Boolean, item: Object, shipment: Object, container: Object },
	data() {
		return {
			/* generic */
			valid: true,
			productSelectKey: 0,
			baseUnits: [],

			currentItem: null,
			currentProduct: null,
			currentVariant: null,

			unitOptions: [],
			activeFormat: null,
			measurementInputs: {},
			scalarValue: null,
			selectedUnitForField: "",
			existingMeasurement: null,

			rules: {
				variant: [(v) => !!v || this.$t("$validation.required", { item: this.$t("variant") })],
				unit: [(v) => !!v || this.$t("$validation.required", { item: this.$t("unit") })],
				quantity: [(v) => v > 0 || this.$t("$validation.required", { item: this.$t("quantity") })],
				amount: [(v) => v !== null || this.$t("$validation.required", { item: this.$t("amount") })],
			},
		};
	},
	watch: {
		value(opened) {
			if (opened) this.initDialog();
			else this.reset();
		},
		"currentItem.unitCode"(code) {
			if (code) this.prepareMeasurementBlock();
		},
	},
	computed: {
		scalarLocked() {
			return this.existingMeasurement && this.existingMeasurement.value != null;
		},
		fieldLocked() {
			return (f) => this.existingMeasurement && this.existingMeasurement[f] != null;
		},
	},
	methods: {
		initDialog() {
			this.currentItem = this.item
				? cloneDeep(this.item)
				: { variantInfo: null, variantId: null, productId: null, name: "", unitCode: "", quantity: null, amount: null };
			if (this.item) {
				this.existingMeasurement = cloneDeep(this.item.measurement);
			}
		},
		reset() {
			this.$refs.form?.reset();
			Object.assign(this.$data, this.$options.data.call(this)); // full reset
		},

		variantChangeAction(v) {
			if (!v) return;
			this.currentItem.variantInfo = v;
			this.currentItem.variantId = v.variantId;
			this.currentItem.productId = v.productId;
			this.loadProductAndVariant();
		},
		loadProductAndVariant() {
			remote.products.find(this.currentItem.productId, {
				onSuccess: (p) => {
					this.currentProduct = p;
					this.currentItem.name = this.currentItem.variantInfo.name;
					this.currentVariant = p.variants.find((vt) => vt._id === this.currentItem.variantId) || null;
					this.mergeUnitDefs();
					if (p.units.length === 1) this.currentItem.unitCode = p.units[0].code;
				},
			});
		},
		mergeUnitDefs() {
			this.unitOptions = this.currentProduct.units.map((u) => ({
				...u,
				...(this.baseUnits.find((b) => b.code === u.code) || {}),
			}));
		},

		prepareMeasurementBlock() {
			if (!this.currentVariant) return;
			const unitDef = this.unitOptions.find((u) => u.code === this.currentItem.unitCode) || {};
			const entry = (unitDef.entryFormats || [])[0] || { inputs: [], label: unitDef.code, _id: null };
			this.activeFormat = entry;
			this.existingMeasurement = (this.currentVariant.measurements || []).find((m) => m.format === entry.label) || null;

			if (entry.inputs.length) {
				this.measurementInputs = {};
				entry.inputs.forEach((f) =>
					this.$set(this.measurementInputs, f, this.existingMeasurement ? this.existingMeasurement[f] ?? null : null)
				);
				this.selectedUnitForField = this.existingMeasurement?.unit || unitDef.code;
			} else {
				this.scalarValue = this.existingMeasurement ? this.existingMeasurement.value : null;
			}
		},
		buildMeasurement() {
			if (!this.activeFormat) return null;

			if (this.activeFormat.inputs.length) {
				const obj = {
					formatId: this.activeFormat._id,
					format: this.activeFormat.label,
					unit: this.selectedUnitForField,
				};
				this.activeFormat.inputs.forEach((f) => (obj[f] = this.measurementInputs[f] ?? null));
				return obj;
			}
			return {
				formatId: this.activeFormat._id,
				format: this.activeFormat.label,
				value: this.scalarValue,
			};
		},

		saveAction() {
			if (!this.$refs.form.validate()) return;
			this.showProgressDialog();

			const { variantId, productId, name } = this.currentItem.variantInfo;

			const unitCode = this.currentItem.unitCode || this.selectedUnitForField;

			const payload = {
				variantId,
				productId,
				unitCode,
				name,
				quantity: this.currentItem.quantity,
				amount: this.currentItem.amount ?? 0,
				measurement: this.buildMeasurement(),
			};

			const onSuccess = (r) => {
				this.hideProgressDialog();
				this.$emit("saved", r);
				this.$emit("input", false);
			};
			const onFail = () => this.hideProgressDialog();

			if (this.currentItem.id)
				remote.shipments.containers.products.update(
					this.shipment.id,
					this.container.id,
					{ ...payload, id: this.currentItem.id },
					{ onSuccess, onFail }
				);
			else
				remote.shipments.containers.products.create(this.shipment.id, this.container.id, payload, {
					onSuccess,
					onFail,
				});
		},
	},
	created() {
		remote.structures.getUnits({ onSuccess: (r) => (this.baseUnits = r.items || []) });
	},
};
</script>
